/**
 * O que o MyMAXX aceita como anexo, e o que faz com cada coisa.
 *
 * Duas listas, e não uma: o tipo que o navegador declara (`mimeType`) e a extensão do
 * nome. Não é redundância — o Chrome no Windows manda `application/octet-stream`, ou
 * string vazia, para extensão que ele não conhece, e KML, KMZ, RAR e MD caem todos nesse
 * caso. Validar só pelo tipo faria o técnico mandar a rota em KMZ e ouvir "formato não
 * aceito" sem entender por quê.
 */
/** O tipo que o navegador manda, quando ele conhece o formato. */
export const TIPOS_ANEXO_ACEITOS = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    // Vídeo: o que a câmera do celular produz. O quicktime é o do iPhone.
    'video/mp4',
    'video/webm',
    'video/quicktime',
    // Áudio gravado no próprio chat. O Chrome grava webm/opus, o Safari mp4/aac — os dois
    // entram, e o worker converte para m4a, que toca em qualquer aparelho.
    'audio/webm',
    'audio/ogg',
    'audio/mp4',
    'audio/mpeg',
    'audio/aac',
    // Documento
    'application/pdf',
    'text/plain',
    'text/markdown',
    'text/html',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    // Planilha, nova e velha. O .xls antigo ainda sai de relógio de ponto e de ERP.
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
    // Apresentação
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // Mapa: rota de fibra e posição de CTO saem do Google Earth assim.
    'application/vnd.google-earth.kml+xml',
    'application/vnd.google-earth.kmz',
    // Compactado: várias fotos de uma instalação de uma vez.
    'application/zip',
    'application/x-zip-compressed',
    'application/vnd.rar',
    'application/x-rar-compressed',
];
/** A extensão, para quando o navegador não souber dizer o tipo. */
export const EXTENSOES_ANEXO_ACEITAS = [
    'jpg',
    'jpeg',
    'png',
    'webp',
    'heic',
    'mp4',
    'webm',
    'mov',
    'ogg',
    'mp3',
    'm4a',
    'aac',
    'pdf',
    'txt',
    'md',
    'html',
    'htm',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'csv',
    'ppt',
    'pptx',
    'kml',
    'kmz',
    'zip',
    'rar',
];
/** A extensão do nome, minúscula e sem o ponto. Vazia quando não há. */
export function extensaoDe(nome) {
    const ponto = nome.lastIndexOf('.');
    if (ponto < 0 || ponto === nome.length - 1)
        return '';
    return nome.slice(ponto + 1).toLowerCase();
}
/**
 * Este arquivo pode ser enviado?
 *
 * Basta o tipo **ou** a extensão estar na lista. Exigir os dois recusaria o KMZ que o
 * Google Earth exporta; aceitar qualquer coisa que tenha extensão conhecida abriria a
 * porta para um executável renomeado — por isso a extensão sozinha só vale quando o
 * navegador não soube dizer o tipo.
 */
export function anexoAceito(mimeType, nome) {
    if (TIPOS_ANEXO_ACEITOS.includes(mimeType))
        return true;
    const tipoDesconhecido = !mimeType || mimeType === 'application/octet-stream';
    return tipoDesconhecido && EXTENSOES_ANEXO_ACEITAS.includes(extensaoDe(nome));
}
export function categoriaDoAnexo(mimeType, nome = '') {
    if (mimeType.startsWith('image/'))
        return 'imagem';
    if (mimeType.startsWith('video/'))
        return 'video';
    if (mimeType.startsWith('audio/'))
        return 'audio';
    const ext = extensaoDe(nome);
    if (mimeType === 'application/pdf' || ext === 'pdf')
        return 'pdf';
    if (/sheet|excel|csv/.test(mimeType) || ['xls', 'xlsx', 'csv'].includes(ext))
        return 'planilha';
    if (/presentation|powerpoint/.test(mimeType) || ['ppt', 'pptx'].includes(ext)) {
        return 'apresentacao';
    }
    if (/google-earth/.test(mimeType) || ['kml', 'kmz'].includes(ext))
        return 'mapa';
    if (/zip|rar|compressed/.test(mimeType) || ['zip', 'rar'].includes(ext))
        return 'compactado';
    if (/word|text\/|markdown|html/.test(mimeType) ||
        ['doc', 'docx', 'txt', 'md', 'html', 'htm'].includes(ext)) {
        return 'documento';
    }
    return 'outro';
}
/** A palavra curta que vai no cartão: "PDF", "Planilha", "Mapa". */
export function rotuloDoAnexo(mimeType, nome = '') {
    const ext = extensaoDe(nome).toUpperCase();
    const categoria = categoriaDoAnexo(mimeType, nome);
    const porCategoria = {
        imagem: 'Imagem',
        video: 'Vídeo',
        audio: 'Áudio',
        pdf: 'PDF',
        planilha: ext === 'CSV' ? 'CSV' : 'Planilha',
        apresentacao: 'Apresentação',
        documento: ext || 'Documento',
        mapa: ext || 'Mapa',
        compactado: ext || 'Compactado',
        outro: ext || 'Arquivo',
    };
    return porCategoria[categoria];
}
/**
 * O navegador consegue mostrar isto sem baixar?
 *
 * Lista fechada, e não "tudo menos o perigoso": formato novo que ninguém previu entra como
 * download, que é o lado seguro de errar.
 *
 * **HTML fica de fora de propósito.** HTML enviado por alguém é programa, não documento:
 * aberto no nosso domínio, ele alcança a sessão de quem clicou. Decisão de 08/10 — entra
 * como anexo, mas só baixa.
 */
export function podeAbrirNoNavegador(mimeType, nome = '') {
    const categoria = categoriaDoAnexo(mimeType, nome);
    if (['imagem', 'video', 'audio', 'pdf'].includes(categoria))
        return true;
    // Texto puro abre; HTML e markdown não — markdown porque abriria como código cru, o que
    // não ajuda ninguém, e HTML pelo motivo acima.
    return mimeType === 'text/plain' && extensaoDe(nome) === 'txt';
}
/** "88 KB", "2,4 MB" — o tamanho como se fala, não em bytes. */
export function tamanhoLegivel(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024)
        return `${Math.round(kb)} KB`;
    return `${(kb / 1024).toFixed(1).replace('.', ',')} MB`;
}
/**
 * O nome encurtado pelo MEIO, não pelo fim.
 *
 * Cortar no fim esconderia a extensão, que é metade do que a pessoa precisa saber — e os
 * nomes que mais precisam de corte são justamente os piores: o PDF que veio do Drive
 * chega com setenta caracteres de base64 e termina em ".pdf" (visto em produção, 08/10).
 */
export function nomeCurto(nome, maximo = 28) {
    if (nome.length <= maximo)
        return nome;
    const ext = extensaoDe(nome);
    const semExt = ext ? nome.slice(0, -(ext.length + 1)) : nome;
    const sobra = maximo - ext.length - 2; // o ponto e a reticência
    if (sobra < 6)
        return `…${nome.slice(-maximo + 1)}`;
    const inicio = Math.ceil(sobra * 0.6);
    const fim = sobra - inicio;
    return `${semExt.slice(0, inicio)}…${semExt.slice(-fim)}${ext ? `.${ext}` : ''}`;
}
/**
 * Este arquivo precisa de alguém para desenhar a prévia?
 *
 * PDF e CSV o navegador resolve sozinho; planilha nova também, com uma biblioteca leve. O
 * que sobra — apresentação, Word, planilha antiga, texto formatado — só abre com algo que
 * entenda o formato, e isso é o LibreOffice num container.
 *
 * Lista fechada: formato novo que ninguém previu fica sem prévia, que custa um cartão sem
 * miniatura em vez de um job que trava a fila tentando converter o que não dá.
 */
export function precisaDePrevia(mimeType, nome = '') {
    const ext = extensaoDe(nome);
    const porExtensao = ['doc', 'docx', 'odt', 'rtf', 'ppt', 'pptx', 'odp', 'xls', 'ods'];
    if (porExtensao.includes(ext))
        return true;
    return /wordprocessingml|presentationml|opendocument|ms-powerpoint|msword|ms-excel/.test(mimeType);
}
//# sourceMappingURL=anexos.js.map